import { type InputHTMLAttributes, forwardRef, useState } from "react";
import { Eye, EyeOff, Copy, Check } from "lucide-react";
import { cn } from "@/lib/cn";

export interface PasswordInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  /** Show a copy-to-clipboard button. */
  copyable?: boolean;
  /** Show a strength meter below the field (reads the current `value`). */
  strengthMeter?: boolean;
  /** Render the error (danger) border state. */
  error?: boolean;
}

/** 0–4 strength score from length + character-class diversity. */
function scorePassword(pw: string): number {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return Math.min(score, 4);
}

const STRENGTH = [
  { label: "Очень слабый", tone: "bg-danger", text: "text-danger" },
  { label: "Слабый", tone: "bg-danger", text: "text-danger" },
  { label: "Средний", tone: "bg-warning", text: "text-warning" },
  { label: "Хороший", tone: "bg-success", text: "text-success" },
  { label: "Сильный", tone: "bg-success", text: "text-success" },
] as const;

/**
 * PasswordInput — Input + reveal toggle (Eye/EyeOff) + optional copy + optional
 * strength meter (DESIGN-SYSTEM §5). Used on Login, Activate, 2FA, telemetr key.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, copyable = false, strengthMeter = false, error = false, value, ...props }, ref) => {
    const [revealed, setRevealed] = useState(false);
    const [copied, setCopied] = useState(false);

    const stringValue = typeof value === "string" ? value : "";
    const score = scorePassword(stringValue);

    async function handleCopy() {
      if (!stringValue) return;
      try {
        await navigator.clipboard.writeText(stringValue);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch {
        /* clipboard may be unavailable; fail silently */
      }
    }

    return (
      <div className="w-full">
        <div className="relative">
          <input
            ref={ref}
            type={revealed ? "text" : "password"}
            value={value}
            className={cn(
              "flex h-10 w-full rounded-lg border bg-bg-secondary px-3 py-2 font-mono text-sm text-fg-primary placeholder:font-sans placeholder:text-fg-muted",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
              "disabled:cursor-not-allowed disabled:opacity-50",
              copyable ? "pr-16" : "pr-10",
              error ? "border-danger" : "border-border",
              className,
            )}
            {...props}
          />
          <div className="absolute inset-y-0 right-0 flex items-center gap-0.5 pr-1.5">
            {copyable && (
              <button
                type="button"
                onClick={handleCopy}
                aria-label={copied ? "Скопировано" : "Копировать"}
                className="rounded p-1 text-fg-muted transition-colors hover:bg-bg-elevated hover:text-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                {copied ? (
                  <Check size={16} strokeWidth={2} className="text-success" aria-hidden="true" />
                ) : (
                  <Copy size={16} strokeWidth={2} aria-hidden="true" />
                )}
              </button>
            )}
            <button
              type="button"
              onClick={() => setRevealed((v) => !v)}
              aria-label={revealed ? "Скрыть пароль" : "Показать пароль"}
              aria-pressed={revealed}
              className="rounded p-1 text-fg-muted transition-colors hover:bg-bg-elevated hover:text-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {revealed ? (
                <EyeOff size={16} strokeWidth={2} aria-hidden="true" />
              ) : (
                <Eye size={16} strokeWidth={2} aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
        {strengthMeter && stringValue.length > 0 && (
          <div className="mt-1.5">
            <div className="flex gap-1">
              {[0, 1, 2, 3].map((i) => (
                <span
                  key={i}
                  className={cn(
                    "h-1 flex-1 rounded-full transition-colors",
                    i < score ? STRENGTH[score].tone : "bg-bg-elevated",
                  )}
                />
              ))}
            </div>
            <p className={cn("mt-1 text-xs", STRENGTH[score].text)}>{STRENGTH[score].label}</p>
          </div>
        )}
      </div>
    );
  },
);
PasswordInput.displayName = "PasswordInput";
